import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    Timestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {db, functions} from './firebase';
import {FeedbackTeam, TeamMember} from '../types/feedback';

const TEAM_MEMBERS_COLLECTION = 'team_members';

export async function getTeamMembers(team?: FeedbackTeam): Promise<TeamMember[]> {
    let q;
    if (team) {
        q = query(
            collection(db, TEAM_MEMBERS_COLLECTION),
            where('team', '==', team),
            orderBy('name', 'asc')
        );
    } else {
        q = query(collection(db, TEAM_MEMBERS_COLLECTION), orderBy('name', 'asc'));
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
    } as TeamMember));
}

export async function getTeamMemberById(id: string): Promise<TeamMember | null> {
    const docRef = doc(db, TEAM_MEMBERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
        return null;
    }

    return {
        id: docSnap.id,
        ...docSnap.data(),
    } as TeamMember;
}

/**
 * L'email etant l'identifiant du document, une lecture directe suffit.
 *
 * La requete sur le champ `email` est conservee en repli : les fiches creees
 * avant le passage a cette convention portent encore un identifiant
 * automatique.
 */
export async function getTeamMemberByEmail(email: string): Promise<TeamMember | null> {
    const id = email.trim().toLowerCase();
    const direct = await getDoc(doc(db, TEAM_MEMBERS_COLLECTION, id));
    if (direct.exists()) {
        return {id: direct.id, ...direct.data()} as TeamMember;
    }

    const q = query(
        collection(db, TEAM_MEMBERS_COLLECTION),
        where('email', '==', id)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
        return null;
    }

    const docSnap = snapshot.docs[0];
    return {
        id: docSnap.id,
        ...docSnap.data(),
    } as TeamMember;
}

export class DuplicateEmailError extends Error {
    constructor(email: string) {
        super(`A team member with email "${email}" already exists`);
        this.name = 'DuplicateEmailError';
    }
}

/**
 * Cree une fiche, **clee par l'email en minuscules**.
 *
 * Surtout pas un identifiant automatique : les regles Firestore autorisent
 * chacun a lire sa ligne en comparant l'identifiant du document a l'email de
 * son jeton. Une fiche clee autrement serait invisible pour l'application, et
 * la personne n'aurait aucun droit — sans le moindre message d'erreur.
 */
export async function createTeamMember(
    data: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
    const id = data.email.trim().toLowerCase();

    // Check for duplicate email
    const existing = await getTeamMemberByEmail(id);
    if (existing) {
        throw new DuplicateEmailError(data.email);
    }

    const docRef = doc(db, TEAM_MEMBERS_COLLECTION, id);
    await setDoc(docRef, {
        ...data,
        email: id,
        // Les droits absents valent faux cote application : on les ecrit
        // explicitement pour qu'ils soient visibles et modifiables dans la
        // console, plutot que devines a la lecture.
        canBeAssigned: data.canBeAssigned ?? true,
        hasSupportAccess: data.hasSupportAccess ?? false,
        hasImmersion: data.hasImmersion ?? false,
        createdAt: Timestamp.now(),
    });
    return docRef.id;
}

/**
 * Met a jour une fiche existante.
 *
 * L'email n'est volontairement pas modifiable : il est l'identifiant du
 * document. Changer d'adresse veut dire creer une fiche et supprimer
 * l'ancienne, faute de quoi les deux divergeraient.
 */
export async function updateTeamMember(
    id: string,
    updates: Partial<Pick<TeamMember,
        'name' | 'team' | 'userGroupId' | 'userGroup' |
        'canBeAssigned' | 'hasSupportAccess' | 'hasImmersion'>>
): Promise<void> {
    const docRef = doc(db, TEAM_MEMBERS_COLLECTION, id);
    await updateDoc(docRef, {
        ...updates,
        updatedAt: Timestamp.now(),
    });
}

export async function deleteTeamMember(id: string): Promise<void> {
    const docRef = doc(db, TEAM_MEMBERS_COLLECTION, id);
    await deleteDoc(docRef);
}

// Admin claim management

interface AdminClaimResponse {
    success: boolean;
    message?: string;
}

interface ListAdminsResponse {
    success: boolean;
    admins: Array<{ uid: string; email: string }>;
    allowlistEmails: string[];
}

export async function setAdminClaim(email: string): Promise<void> {
    const fn = httpsCallable<{ email: string }, AdminClaimResponse>(functions, 'setAdminClaim');
    const result = await fn({ email });
    if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to set admin claim');
    }
}

export async function removeAdminClaim(email: string): Promise<void> {
    const fn = httpsCallable<{ email: string }, AdminClaimResponse>(functions, 'removeAdminClaim');
    const result = await fn({ email });
    if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to remove admin claim');
    }
}

export async function listAdmins(): Promise<Set<string>> {
    const fn = httpsCallable<Record<string, never>, ListAdminsResponse>(functions, 'listAdmins');
    const result = await fn({});
    const adminEmails = new Set<string>();
    if (result.data.success) {
        result.data.admins.forEach((a) => adminEmails.add(a.email.toLowerCase()));
        result.data.allowlistEmails.forEach((e: string) => adminEmails.add(e.toLowerCase()));
    }
    return adminEmails;
}
